/**
 * The buyer's country, handed to the Payment Element at CREATION.
 *
 * Why this exists: Klarna eligibility is country-dependent, and an element that
 * does not yet know the buyer's country parks Klarna last regardless of the
 * `paymentMethodOrder` we passed. Live readout 2026-09-06 (US, Chrome): the
 * element was created with `paymentMethodOrder=[card, klarna, afterpay_clearpay]`
 * and still rendered Card, Afterpay, Bank (ACH), Klarna, because the only
 * country signal reached it from the typed billing form, i.e. after creation.
 * Seeding `defaultValues.billingDetails.address.country` gives the element the
 * country it already knows from geo before it decides what to render.
 *
 * `defaultValues` is an INITIAL value only: a country the buyer picks in the
 * form still wins, and nothing here is ever read by charge code. Display/UI
 * only - it touches no amount and no currency.
 */

/**
 * A billing country usable as a Payment Element default: an ISO-3166-1 alpha-2
 * code, upper-cased. Anything else (empty, a full country name, a stray value
 * from storage) yields `undefined` so the caller omits the option entirely.
 */
export function normalizeBillingCountry(country: string | undefined | null): string | undefined {
  const trimmed = (country || "").trim();
  return /^[A-Za-z]{2}$/.test(trimmed) ? trimmed.toUpperCase() : undefined;
}

/** The `defaultValues` shape the Payment Element accepts. */
export type PaymentElementDefaultValues = {
  billingDetails: { address: { country: string } };
};

/**
 * The `defaultValues` to pass to the Payment Element, or `undefined` when the
 * key must be omitted entirely.
 *
 * With `installmentsEnabled` false - or with no usable country - the caller
 * spreads `undefined` into the options object, which is byte-for-byte the
 * options the surface had before instalments existed.
 */
export function paymentElementDefaultValuesFor(
  installmentsEnabled: boolean,
  country: string | undefined | null,
): PaymentElementDefaultValues | undefined {
  if (!installmentsEnabled) return undefined;
  const code = normalizeBillingCountry(country);
  return code ? { billingDetails: { address: { country: code } } } : undefined;
}
